/**
 * Server functions do banner rotativo da home.
 *  - getBannerConfigFn: pública (loader SSR da home)
 *  - setBannerConfigFn: apenas admin (/admin/banner)
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin/auth.server";
import {
  BANNER_CONFIG_KEY,
  BANNER_SLIDE_KEYS,
  normalizeBannerConfig,
  type BannerConfig,
} from "@/lib/home/banner";
import { readBannerConfigServer } from "@/lib/home/banner.server";

export const getBannerConfigFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<BannerConfig> => readBannerConfigServer(),
);

const ConfigSchema = z.object({
  order: z.array(z.string()).max(50),
  enabled: z.record(z.string(), z.boolean()),
});

export const setBannerConfigFn = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => ConfigSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "/admin/banner");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const config = normalizeBannerConfig(data);

    const { error } = await supabaseAdmin.from("site_config").upsert(
      {
        key: BANNER_CONFIG_KEY,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: config as any,
        updated_by: context.userId,
      },
      { onConflict: "key" },
    );
    if (error) throw new Error(error.message);

    const ativos = config.order.filter((k) => config.enabled[k]);
    await supabaseAdmin.rpc("log_operation_event", {
      _event_type: "banner_config",
      _category: "config",
      _title: "Banner da home atualizado",
      _severity: "info",
      _description: `${ativos.length} de ${BANNER_SLIDE_KEYS.length} slides no ar: ${ativos.join(", ") || "nenhum"}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      _metadata: config as any,
      _created_by: context.userId,
    });

    return { ok: true, config };
  });
